import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { pathWithRegion } from '../../utils/navBase';
import { PUBLIC_REGION_CODES, REGION_HREFLANG } from '../../config/publicRegions';
import { useSeoExtras } from '../../context/SiteSeoContext';

function getSiteUrl(fallbackFromSettings) {
  if (fallbackFromSettings) return String(fallbackFromSettings).replace(/\/+$/, '');
  if (process.env.REACT_APP_SITE_URL) return process.env.REACT_APP_SITE_URL.replace(/\/+$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return 'https://fliqmatch.com';
}

export default function SEO({
  title,
  description = '',
  path = '/',
  region = '',
  image = '',
  noindex = false,
  keywords = [],
  jsonLd = null,
  canonicalOverride = '',
  hreflangAlternates = null,
}) {
  const {
    siteName,
    siteUrl: settingsSiteUrl,
    twitterSite,
    googleSiteVerification,
    bingSiteVerification,
    hreflangEnabled,
  } = useSeoExtras();

  const site = getSiteUrl(settingsSiteUrl);
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const currentPath = path.startsWith('http') ? path : pathWithRegion(cleanPath, region);
  const canonical =
    canonicalOverride ||
    (path.startsWith('http') ? path : `${site}${currentPath}`);
  const defaultImage = `${site}/logo192.png`;
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';
  const locale = REGION_HREFLANG[region] || 'en-US';
  const htmlLang = String(locale).split('-')[0] || 'en';
  const pageTitle = title && !String(title).includes(siteName) ? `${title} | ${siteName}` : title || siteName;

  const hadPrerenderMeta = useRef(
    typeof document !== 'undefined' &&
      !!document.querySelector('meta[name="prerender-status"][content="ok"]'),
  );
  const hadPrerenderHreflang = useRef(
    typeof document !== 'undefined' &&
      !!document.querySelector('link[rel="alternate"][hreflang]'),
  );
  const [manageHeadTags, setManageHeadTags] = useState(!hadPrerenderMeta.current);

  useEffect(() => {
    if (!hadPrerenderMeta.current) return;
    if (canonicalOverride || currentPath) {
      document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    }
    setManageHeadTags(true);
  }, [title, canonicalOverride, currentPath]);

  const serverAlternates =
    Array.isArray(hreflangAlternates) && hreflangAlternates.length ? hreflangAlternates : null;
  const renderClientHreflang = hreflangEnabled && !hadPrerenderHreflang.current;

  const fallbackGoogleVerification =
    process.env.REACT_APP_GOOGLE_SITE_VERIFICATION ||
    'vlT79hkQHrBlxERH0wtA_XPjjArx8OwBXUJqXomv-sw';
  const googleVerification = googleSiteVerification || fallbackGoogleVerification;

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{pageTitle}</title>
      {googleVerification ? (
        <meta name="google-site-verification" content={googleVerification} />
      ) : null}
      {bingSiteVerification ? <meta name="msvalidate.01" content={bingSiteVerification} /> : null}
      {manageHeadTags ? (
        <>
          <meta name="description" content={description} />
          {keywords.length ? <meta name="keywords" content={keywords.join(', ')} /> : null}
          <meta name="robots" content={robots} />
          <link rel="canonical" href={canonical} />

          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonical} />
          <meta property="og:image" content={image || defaultImage} />
          <meta property="og:locale" content={locale} />
          <meta property="og:site_name" content={siteName} />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={image || defaultImage} />
          {twitterSite ? <meta name="twitter:site" content={twitterSite} /> : null}

          {renderClientHreflang && serverAlternates
            ? serverAlternates.map((alt) => (
                <link
                  key={`${alt.hreflang}-${alt.href || alt.path}`}
                  rel="alternate"
                  hrefLang={alt.hreflang}
                  href={alt.href || `${site}${alt.path || ''}`}
                />
              ))
            : null}
          {renderClientHreflang && !serverAlternates
            ? PUBLIC_REGION_CODES.map((r) => {
                const href = `${site}${pathWithRegion(cleanPath, r === 'us' ? '' : r)}`;
                const hreflang = REGION_HREFLANG[r] || 'en-US';
                return <link key={r} rel="alternate" hrefLang={hreflang} href={href} />;
              })
            : null}
          {renderClientHreflang && !serverAlternates ? (
            <link rel="alternate" hrefLang="x-default" href={`${site}${cleanPath}`} />
          ) : null}
        </>
      ) : null}
      {(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []).map((entry, idx) => (
        <script key={`jsonld-${idx}`} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}
